import { BillingProductType } from '@citruscamps/citrus-client'
import { DateTime } from 'luxon'
import React from 'react'
import { NavLink } from 'react-router-dom'
import { DashboardLayout } from '../../../../components/DashboardLayout/DashboardLayout'
import { DateMacroSelect } from '../../../../components/DateMacroSelect/DateMacroSelect'
import { useFlags } from '../../../../components/FeatureFlag/hooks/useFlags'
import { FinancialNavTabs } from '../../../../components/FinancialNavTabs/FinancialNavTabs'
import { Header } from '../../../../components/Header/Header'
import { LoadingListPage } from '../../../../components/LoadingListPage/LoadingListPage'
import { Title } from '../../../../components/Title/Title'
import { TrendPlot } from '../../../../components/TrendPlot/TrendPlot'
import { TruncatedText } from '../../../../components/TruncatedText/TruncatedText'
import { ROUTES } from '../../../../constants/routes'
import { useAuth } from '../../../../hooks/useAuth'
import { getIntervalByDateMacro, getIntervalByQuery } from '../../../../utils/date-macro'
import { toQueryParams } from '../../../../utils/query-params'
import { useFetchProgramAnalytics } from '../../../Dashboard/pages/Dashboard/hooks/useFetchProgramAnalytics'
import { FinancialReportIdentifier } from '../FinancialReportDetails/hooks/useFetchFinancialReport'

interface ReportListItem {
  id: FinancialReportIdentifier
  name: string
  description: string
  plan?: BillingProductType
}

export const FinancialReportList: ReportListItem[] = [
  {
    id: 'sales_summary',
    name: 'Sales summary report',
    description: 'A summary of your sales and payouts generated through Citrus.',
  },
  {
    id: 'sales_finance',
    name: 'Sales item report',
    description: 'A breakdown of the sales for each item sold on an order.',
  },
  {
    id: 'taxes',
    name: 'Taxes report',
    description: 'The amount collected for each of your pre-configured taxes.',
  },
  {
    id: 'payments',
    name: 'Payments report',
    description: 'A detailed summary of the sales collected for each payment method.',
  },
  {
    id: 'payout_reconciliation',
    name: 'Payout reconciliation report',
    description:
      'The transactions processed in Citrus and the corresponding payouts that are available in Stripe.',
  },
  {
    id: 'registrations',
    name: 'Registration report',
    plan: 'pro',
    description: 'A breakdown of sales for each registration placed.',
  },
  {
    id: 'memberships',
    name: 'Membership report',
    plan: 'pro',
    description: 'A breakdown of sales for each active membership.',
  },
]

export const FinancialReports = (): React.ReactElement => {
  const { program, isSwitching } = useAuth()
  const { flags } = useFlags()
  const {
    analytics,
    isLoading,
    dateQuery: dateMacro,
    setDateQuery: setDateMacro,
  } = useFetchProgramAnalytics({
    programId: program?.id,
    initialDateQuery: { date_macro: 'last7days', unit: 'daily' },
  })
  const interval = getIntervalByQuery(dateMacro, program)
  const query = toQueryParams({
    date_macro: dateMacro.date_macro,
    start_date: dateMacro.start_date,
    end_date: dateMacro.end_date,
    timezone: dateMacro.timezone,
  })

  if (!program || isSwitching) {
    return (
      <DashboardLayout>
        <LoadingListPage />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Title value="Reports" />
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <Header
              subtitle="Reports"
              title="Financials"
              breadcrumbs={[{ to: ROUTES.FINANCIALS, label: 'Financials' }, { label: 'Reports' }]}
            />
            <div className="row align-items-center">
              <div className="col">
                <FinancialNavTabs />
              </div>
            </div>
            <div className="row my-5">
              <div className="col">
                <h4 className="d-md-flex text-md-start mb-4 text-center">Financial Overview</h4>
                <DateMacroSelect
                  values={dateMacro}
                  program={program}
                  onSelectMacro={(value) => setDateMacro(value)}
                />
                <div className="row g-0">
                  <div className="col-md-6">
                    <TrendPlot
                      title="Gross volume"
                      data={analytics?.gross_volume.map((v) => ({ x: v.x, y: v.y.major_value }))}
                      unit="currency"
                      step={analytics?.unit}
                      loading={isLoading}
                      currency={
                        analytics?.gross_volume.find((v) => v.y.currency_code)?.y.currency_code
                      }
                      language={program?.language}
                      timezone={program.timezone}
                    />
                  </div>
                  <div className="col-md-6">
                    <TrendPlot
                      title="Gross taxed volume"
                      data={analytics?.gross_taxed_volume.map((v) => ({
                        x: v.x,
                        y: v.y.major_value,
                      }))}
                      unit="currency"
                      step={analytics?.unit}
                      loading={isLoading}
                      currency={
                        analytics?.gross_taxed_volume.find((v) => v.y.currency_code)?.y
                          .currency_code
                      }
                      language={program?.language}
                      timezone={program.timezone}
                    />
                  </div>
                </div>
                <h4 className="d-md-flex text-md-start my-4 text-center">Reports</h4>
                {FinancialReportList.map((report) => {
                  const isUnableView: boolean =
                    (!!flags?.months_of_history &&
                      !!interval.start &&
                      interval.start <
                        DateTime.local({ zone: interval.start.zone }).minus({
                          months: flags?.months_of_history,
                        })) ||
                    (report.id === 'registrations' && !flags?.registration_report_enabled) ||
                    (report.id === 'memberships' && !flags?.membership_report_enabled)

                  return (
                    <div key={report.id}>
                      <hr className="my-3" />
                      <div className="row">
                        <div className="col-10 col-lg-10">
                          <TruncatedText
                            className={`h6 mb-1 ${isUnableView ? 'text-body-secondary' : ''}`}
                          >
                            {report.name}
                            {isUnableView && !!report.plan && (
                              <span className="badge text-bg-secondary mx-2">
                                {report.plan.charAt(0).toUpperCase() +
                                  report.plan.slice(1).toLowerCase()}{' '}
                                Plan
                              </span>
                            )}
                          </TruncatedText>
                          <TruncatedText className="text-body-secondary small mb-0">
                            {report.description || '\u00A0'}
                          </TruncatedText>
                          <TruncatedText className="text-body-secondary small mb-0">
                            {(() => {
                              const interval = getIntervalByDateMacro(dateMacro, program)
                              return !!interval.end &&
                                !!interval.start?.hasSame(interval.end, 'day')
                                ? interval.start?.toLocaleString(DateTime.DATE_MED)
                                : [
                                    interval.start?.toLocaleString(DateTime.DATE_MED),
                                    interval.end?.toLocaleString(DateTime.DATE_MED),
                                  ].join(' · ')
                            })() || '\u00A0'}
                          </TruncatedText>
                        </div>
                        <div className="col-2 col-lg-2 text-end">
                          <NavLink
                            to={
                              ROUTES.FINANCIALS_REPORT_DETAIL.replace(':type', report.id) +
                              (query ? `?${query}` : '')
                            }
                            className={`btn btn-link${isUnableView ? ' disabled' : ''}`}
                          >
                            View
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
